import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ExternalLink, ArrowLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n, tr } from "@/lib/i18n";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import ProductCard from "@/components/ProductCard";

export default function ProductDetailPage() {
  const { slug } = useParams();
  const { t, lang } = useI18n();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    setProduct(null);
    setNotFound(false);
    api
      .get(`/products/${slug}`)
      .then((r) => {
        setProduct(r.data);
        document.title = r.data.meta_title || `${r.data.name} — BioNutrix`;
        const desc = document.querySelector('meta[name="description"]');
        if (desc && r.data.meta_description) desc.setAttribute("content", r.data.meta_description);
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24 text-center">
        <h1 className="font-display text-4xl text-brand mb-4">Prodotto non trovato</h1>
        <Link to="/prodotti" className="link-underline text-brand">Torna al catalogo</Link>
      </div>
    );
  }
  if (!product) {
    return <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24 text-muted-ink">{t("common.loading")}</div>;
  }

  const gallery = product.gallery?.length ? product.gallery : [product.image];
  const badges = tr(product, lang, "badges") || product.badges || [];
  const benefits = tr(product, lang, "benefits") || product.benefits || [];
  const category = tr(product, lang, "category") || product.category;
  const name = tr(product, lang, "name") || product.name;
  const shortDesc = tr(product, lang, "short_description");
  const longDesc = tr(product, lang, "long_description");
  const quantity = tr(product, lang, "quantity_label") || product.quantity_label;
  const accent = product.line === "pet" ? "pet" : "human";
  const accentColor = product.line === "pet" ? "var(--pet)" : "var(--human)";

  return (
    <div className="pb-28 md:pb-16" data-testid="product-detail-page">
      {/* Breadcrumb */}
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-8">
        <nav className="flex items-center gap-1.5 text-xs text-muted-ink" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-brand">{t("nav.home")}</Link>
          <ChevronRight className="h-3 w-3" />
          <Link to="/prodotti" className="hover:text-brand">{t("nav.products")}</Link>
          <ChevronRight className="h-3 w-3" />
          <Link to={`/prodotti?linea=${product.line}`} className="hover:text-brand">
            {product.line === "pet" ? t("nav.pet") : t("nav.human")}
          </Link>
          <ChevronRight className="h-3 w-3" />
          <span className="text-brand">{name}</span>
        </nav>
      </div>

      {/* Main */}
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-8 lg:pt-12 grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-16">
        {/* Gallery */}
        <div className="md:col-span-6">
          <div className="md:sticky md:top-24">
            <div className="aspect-[4/5] bg-cream-warm overflow-hidden" data-testid="product-main-image">
              <img src={gallery[activeImg]} alt={name} className="w-full h-full object-cover" />
            </div>
            {gallery.length > 1 && (
              <div className="mt-3 flex gap-2">
                {gallery.map((g, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImg(i)}
                    className={`h-16 w-16 border ${i === activeImg ? "border-brand" : "border-soft"} bg-cream-warm overflow-hidden`}
                    aria-label={`Immagine ${i + 1}`}
                  >
                    <img src={g} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="md:col-span-6">
          <div className="flex items-center gap-2 text-xs uppercase tracking-editorial mb-4" style={{ color: accentColor }}>
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: accentColor }} />
            {product.line === "pet" ? "Linea Pet" : "Linea Human"} · {category}
          </div>
          <h1 className="font-display text-5xl md:text-6xl text-brand leading-none">{name}</h1>
          <p className="mt-4 text-lg text-muted-ink leading-relaxed">{shortDesc}</p>

          {/* Badges */}
          <div className="mt-6 flex flex-wrap gap-2">
            {badges.map((b) => (
              <span key={b} className="text-[11px] px-2.5 py-1 border border-soft text-brand">{b}</span>
            ))}
          </div>

          {/* Amazon CTA desktop */}
          {product.amazon_url && (
            <div className="hidden md:block mt-8 border border-soft bg-white p-5">
              <p className="text-xs uppercase tracking-editorial text-muted-ink mb-2">
                {t("product.buy_amazon_sub")}
              </p>
              <a
                href={product.amazon_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="amazon-buy-button"
                className="inline-flex items-center gap-2 rounded-full bg-brand text-white px-6 py-3 hover:bg-[#0f2020] transition-colors"
              >
                {t("product.buy_amazon")}
                <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          )}

          {/* Benefits */}
          {benefits.length > 0 && (
            <div className="mt-10">
              <h2 className="font-display text-2xl text-brand mb-4">{t("product.areas")}</h2>
              <ul className="space-y-2">
                {benefits.map((b, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm">
                    <span className="mt-2 h-1 w-6 shrink-0" style={{ background: accentColor }} />
                    <span className="text-brand">{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Long description */}
          {longDesc && (
            <div className="mt-10">
              <h2 className="font-display text-2xl text-brand mb-3">{t("product.formulation")}</h2>
              <div className="text-sm text-muted-ink leading-relaxed whitespace-pre-line">{longDesc}</div>
            </div>
          )}

          {/* Ingredients */}
          {product.ingredients_composition && (
            <div className="mt-10">
              <h2 className="font-display text-2xl text-brand mb-3">{t("product.ingredients")}</h2>
              <p className="text-sm text-muted-ink leading-relaxed">{product.ingredients_composition}</p>
            </div>
          )}

          {/* Analytical components table */}
          {product.analytical_components?.length > 0 && (
            <div className="mt-8">
              <h3 className="font-display text-xl text-brand mb-3">{t("product.analytical")}</h3>
              <div className="border border-soft">
                <table className="w-full text-sm">
                  <tbody>
                    {product.analytical_components.map((c, i) => (
                      <tr key={i} className="border-b border-soft last:border-b-0">
                        <td className="px-4 py-2.5 text-brand">{c.name}</td>
                        <td className="px-4 py-2.5 text-right text-muted-ink font-mono text-xs">{c.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Dosage */}
          {product.dosage?.length > 0 && (
            <div className="mt-10">
              <h2 className="font-display text-2xl text-brand mb-3">{t("product.how_to_use")}</h2>
              <div className="border border-soft">
                {product.dosage.map((d, i) => (
                  <div key={i} className="flex flex-col md:flex-row md:items-center md:justify-between p-4 border-b border-soft last:border-b-0 gap-1">
                    <div className="text-brand text-sm">{d.group}</div>
                    <div className="text-muted-ink text-sm font-medium">{d.amount}</div>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-xs text-muted-ink italic">
                {lang === "en"
                  ? "Always cross-check the dosage with the official label on the packaging: the label prevails in case of differences."
                  : "Confronta sempre le modalità d'uso con l'etichetta ufficiale della confezione: in caso di differenze prevale l'etichetta."}
              </p>
            </div>
          )}

          {/* Warnings */}
          {product.warnings?.length > 0 && (
            <div className="mt-10">
              <h2 className="font-display text-2xl text-brand mb-3">{t("product.warnings")}</h2>
              <ul className="space-y-2 text-sm">
                {product.warnings.map((w, i) => (
                  <li key={i} className="flex items-start gap-3 text-muted-ink">
                    <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-muted-ink" />
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Quality */}
          <div className="mt-10 border-t border-soft pt-8">
            <h2 className="font-display text-2xl text-brand mb-3">{t("product.quality")}</h2>
            <p className="text-sm text-muted-ink leading-relaxed">{t("product.quality_txt")}</p>
          </div>

          {/* FAQs */}
          {product.faqs?.length > 0 && (
            <div className="mt-10">
              <h2 className="font-display text-2xl text-brand mb-3">{t("product.faq")}</h2>
              <Accordion type="single" collapsible className="border-t border-soft">
                {product.faqs.map((f) => (
                  <AccordionItem key={f.id} value={f.id} className="border-b border-soft">
                    <AccordionTrigger className="text-brand hover:no-underline text-left">
                      {f.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-ink text-sm">{f.answer}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          )}

          {/* Disclaimer */}
          <div className="mt-10 border border-soft bg-cream-warm p-5">
            <p className="text-xs text-muted-ink leading-relaxed italic">{t("product.disclaimer")}</p>
          </div>
        </div>
      </div>

      {/* Related */}
      {product.related?.length > 0 && (
        <section className="mx-auto max-w-[1400px] px-6 lg:px-10 py-16 mt-8">
          <h2 className="font-display text-3xl text-brand mb-8">{t("product.related")}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl">
            {product.related.map((r) => (
              <ProductCard key={r.slug} product={r} />
            ))}
          </div>
        </section>
      )}

      {/* Mobile sticky Amazon */}
      {product.amazon_url && (
        <div
          className="md:hidden fixed bottom-0 left-0 right-0 z-40 backdrop-blur-xl bg-white/90 border-t border-soft p-3"
          data-testid="mobile-sticky-amazon-buy"
        >
          <a
            href={product.amazon_url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full rounded-full bg-brand text-white h-12"
          >
            {t("product.buy_amazon")} <ExternalLink className="h-4 w-4" />
          </a>
        </div>
      )}
    </div>
  );
}
